import { createFileRoute, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { agregarViajeroCheckin, finalizarCheckin, getReservaCheckin } from "@/lib/checkin.functions";
import { CheckCircle2, UserPlus, Users } from "lucide-react";

export const Route = createFileRoute("/checkin/$token")({
  head: () => ({
    meta: [
      { title: "Check-in online · El Pajar de Benito" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CheckinPage,
});

type Reserva = Awaited<ReturnType<typeof getReservaCheckin>>;

function CheckinPage() {
  const { token } = useParams({ from: "/checkin/$token" });
  const getFn = useServerFn(getReservaCheckin);
  const addFn = useServerFn(agregarViajeroCheckin);
  const finalizeFn = useServerFn(finalizarCheckin);
  const [reserva, setReserva] = useState<Reserva>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await getFn({ data: { token } });
      setReserva(r);
    } catch (err) {
      toast.error("No se pudo cargar la reserva", { description: (err as Error).message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [token]);

  if (loading) {
    return <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">Cargando…</div>;
  }
  if (!reserva) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="font-serif text-2xl">Enlace no válido</h1>
        <p className="mt-2 text-muted-foreground">Este enlace de check-in ha caducado o no existe. Si necesitas ayuda escríbenos.</p>
      </div>
    );
  }

  const ocupados = reserva.viajeros.length;
  const restantes = Math.max(0, reserva.num_huespedes - ocupados);
  const completado = reserva.checkin_completado_at !== null;

  async function onFinalize() {
    setSaving(true);
    try {
      await finalizeFn({ data: { token } });
      toast.success("¡Check-in completado! Nos vemos pronto.");
      await refresh();
    } catch (err) {
      toast.error("No se pudo finalizar", { description: (err as Error).message });
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen bg-secondary/30 py-10">
      <div className="mx-auto max-w-2xl space-y-6 px-4">
        <header className="rounded-xl border bg-card p-6 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Check-in online</p>
          <h1 className="mt-1 font-serif text-2xl text-primary">Hola {reserva.nombre_huesped.split(" ")[0]}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {reserva.habitacion_nombre ?? "Tu alojamiento"} · del{" "}
            <strong className="text-foreground">{formatDate(reserva.fecha_entrada)}</strong> al{" "}
            <strong className="text-foreground">{formatDate(reserva.fecha_salida)}</strong>
          </p>
          <p className="mt-3 flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-muted-foreground" />
            {ocupados} de {reserva.num_huespedes} viajeros registrados
          </p>
        </header>

        {completado ? (
          <div className="rounded-xl border border-green-200 bg-green-50 p-6 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
            <h2 className="mt-2 font-serif text-lg text-green-900">Check-in completado</h2>
            <p className="mt-1 text-sm text-green-800">Ya está todo listo. Te esperamos el día de la llegada.</p>
          </div>
        ) : (
          <>
            <ViajerosList viajeros={reserva.viajeros} />
            {restantes > 0 ? (
              <NuevoViajeroForm token={token} onSaved={refresh} defaultRol={ocupados === 0 ? "titular" : "viajero"} />
            ) : (
              <div className="rounded-xl border bg-card p-6">
                <p className="text-sm text-muted-foreground">Has registrado ya a todos los viajeros de la reserva.</p>
                <Button className="mt-4 w-full" onClick={onFinalize} disabled={saving}>
                  {saving ? "Guardando…" : "Finalizar check-in"}
                </Button>
              </div>
            )}
          </>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Estos datos se recogen para cumplir con el registro obligatorio de viajeros (RD 933/2021).
        </p>
      </div>
    </div>
  );
}

function ViajerosList({ viajeros }: { viajeros: NonNullable<Reserva>["viajeros"] }) {
  if (viajeros.length === 0) return null;
  return (
    <div className="rounded-xl border bg-card p-4">
      <h2 className="mb-3 text-sm font-medium">Viajeros registrados</h2>
      <ul className="divide-y">
        {viajeros.map((v) => (
          <li key={v.id} className="flex items-center justify-between py-2 text-sm">
            <div>
              <p className="font-medium">{v.nombre} {v.apellido1} {v.apellido2 ?? ""}</p>
              <p className="text-xs text-muted-foreground">{v.tipo_documento} {v.numero_documento} · {v.nacionalidad}</p>
            </div>
            {v.rol === "titular" && (
              <span className="rounded bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary">Titular</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function NuevoViajeroForm({ token, onSaved, defaultRol }: { token: string; onSaved: () => void; defaultRol: "titular" | "viajero" }) {
  const addFn = useServerFn(agregarViajeroCheckin);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    rol: defaultRol,
    nombre: "",
    apellido1: "",
    apellido2: "",
    sexo: "" as "" | "H" | "M" | "O",
    fecha_nacimiento: "",
    nacionalidad: "ESP",
    tipo_documento: "DNI" as "DNI" | "NIE" | "PAS" | "OTRO",
    numero_documento: "",
    fecha_expedicion: "",
    direccion: "",
    ciudad: "",
    codigo_postal: "",
    pais_residencia: "ESP",
    telefono: "",
    email: "",
  });

  function upd<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await addFn({
        data: {
          token,
          rol: form.rol,
          nombre: form.nombre,
          apellido1: form.apellido1,
          apellido2: form.apellido2 || null,
          sexo: form.sexo || null,
          fecha_nacimiento: form.fecha_nacimiento,
          nacionalidad: form.nacionalidad,
          tipo_documento: form.tipo_documento,
          numero_documento: form.numero_documento,
          fecha_expedicion: form.fecha_expedicion || null,
          direccion: form.direccion || null,
          ciudad: form.ciudad || null,
          codigo_postal: form.codigo_postal || null,
          pais_residencia: form.pais_residencia || null,
          telefono: form.telefono || null,
          email: form.email || null,
          parentesco: null,
        },
      });
      toast.success("Viajero registrado");
      setForm({ ...form, nombre: "", apellido1: "", apellido2: "", numero_documento: "", fecha_nacimiento: "", telefono: "", email: "", rol: "viajero" });
      onSaved();
    } catch (err) {
      toast.error("No se pudo registrar", { description: (err as Error).message });
    } finally { setSaving(false); }
  }

  return (
    <form onSubmit={onSubmit} className="rounded-xl border bg-card p-6 space-y-4">
      <div className="flex items-center gap-2">
        <UserPlus className="h-4 w-4 text-primary" />
        <h2 className="font-medium">Añadir viajero {form.rol === "titular" ? "(titular)" : ""}</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Nombre *"><Input required value={form.nombre} onChange={(e) => upd("nombre", e.target.value)} /></Field>
        <Field label="Primer apellido *"><Input required value={form.apellido1} onChange={(e) => upd("apellido1", e.target.value)} /></Field>
        <Field label="Segundo apellido"><Input value={form.apellido2} onChange={(e) => upd("apellido2", e.target.value)} /></Field>
        <Field label="Fecha de nacimiento *"><Input type="date" required value={form.fecha_nacimiento} onChange={(e) => upd("fecha_nacimiento", e.target.value)} /></Field>
        <Field label="Sexo">
          <Select value={form.sexo} onValueChange={(v) => upd("sexo", v as typeof form.sexo)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="H">Hombre</SelectItem>
              <SelectItem value="M">Mujer</SelectItem>
              <SelectItem value="O">Otro</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Nacionalidad (ISO 3) *"><Input required maxLength={3} value={form.nacionalidad} onChange={(e) => upd("nacionalidad", e.target.value.toUpperCase())} /></Field>
        <Field label="Tipo documento *">
          <Select value={form.tipo_documento} onValueChange={(v) => upd("tipo_documento", v as typeof form.tipo_documento)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="DNI">DNI</SelectItem>
              <SelectItem value="NIE">NIE</SelectItem>
              <SelectItem value="PAS">Pasaporte</SelectItem>
              <SelectItem value="OTRO">Otro</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Número documento *"><Input required value={form.numero_documento} onChange={(e) => upd("numero_documento", e.target.value.toUpperCase())} /></Field>
        <Field label="Fecha de expedición"><Input type="date" value={form.fecha_expedicion} onChange={(e) => upd("fecha_expedicion", e.target.value)} /></Field>
        <Field label="País de residencia (ISO 3)"><Input maxLength={3} value={form.pais_residencia} onChange={(e) => upd("pais_residencia", e.target.value.toUpperCase())} /></Field>
        <Field label="Dirección"><Input value={form.direccion} onChange={(e) => upd("direccion", e.target.value)} /></Field>
        <Field label="Ciudad"><Input value={form.ciudad} onChange={(e) => upd("ciudad", e.target.value)} /></Field>
        <Field label="Código postal"><Input value={form.codigo_postal} onChange={(e) => upd("codigo_postal", e.target.value)} /></Field>
        <Field label="Teléfono"><Input value={form.telefono} onChange={(e) => upd("telefono", e.target.value)} /></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} /></Field>
      </div>
      <Button type="submit" className="w-full" disabled={saving}>{saving ? "Guardando…" : "Registrar viajero"}</Button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function formatDate(iso: string) {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}